import maths from '@/business/utils/maths';
import { ROUTE_MODULE } from '@/store/modules/routeModule';

class BaseModulePlugins {
  constructor({ module, paramId, store }) {
    this.module = module;
    this.paramId = paramId;
    this.store = store;
  }

  onInit(fn) {
    this.store.subscribe((mutation) => {
      if (mutation.type === 'boilerplate/SET_INIT' && mutation.payload) {
        fn();
      }
    });
  }

  onItemsLoaded(fn, module = this.module) {
    this.store.subscribe((mutation) => {
      if (mutation.type === `${module}/SET_ITEMS` && mutation.payload) {
        fn(mutation.payload);
      }
    });
  }

  setCurrentLoading() {
    this.store.commit(`${this.module}/SET_CURRENT_REQUEST`);
  }

  onRouteParamIdChange(fn, paramId = this.paramId) {
    this.store.watch(
      (state) => {
        return state[ROUTE_MODULE].params[paramId];
      },
      (id) => {
        if (id) {
          fn(id);
        }
      },
      { immediate: true }
    );
  }

  loadCurrentOnRouteParamsChange(nestedParamId = this.paramId) {
    this.onRouteParamIdChange(
      (id) => this.store.dispatch(`${this.module}/initCurrent`, id),
      nestedParamId
    );
  }

  loadItemsOnRouteParamsChange(nestedParamId = this.paramId) {
    this.onRouteParamIdChange((id) => {
      this.store.dispatch(`${this.module}/setUrlParamIdAndClearFilters`, id);
    }, nestedParamId);
  }

  loadItemsOnCurrentEntityChange() {
    this.store.subscribe((mutation) => {
      if (mutation.type === 'entities/SET_CURRENT') {
        this.store.dispatch(`${this.module}/setItemsPage`, 1);
      }
    });
  }

  onCurrentEntityChange(fn) {
    this.store.subscribe((mutation) => {
      if (mutation.type === 'entities/SET_CURRENT') {
        fn();
      }
    });
  }

  onCurrentPropertyChange(fn) {
    this.store.subscribe((mutation) => {
      if (mutation.type === 'properties_360/SET_CURRENT') {
        fn(mutation.payload);
      }
    });
  }

  onItemAddedOrModified(fn, module) {
    this.store.subscribe((mutation) => {
      if (
        mutation.type === `${module || this.module}/UPDATE_SUCCESS` ||
        mutation.type === `${module || this.module}/CREATE_SUCCESS`
      ) {
        fn(mutation.payload);
      }
    });
  }

  onItemDeleted(fn, module) {
    this.store.subscribe((mutation) => {
      if (mutation.type === `${module || this.module}/DELETE_SUCCESS`) {
        fn(mutation.payload);
      }
    });
  }

  onActiveRequestChange(f) {
    this.store.subscribe((mutation) => {
      if (mutation.type === 'requests/SET_CURRENT_ACTIVE') {
        f(mutation.payload);
      }
    });
  }

  persistVuexMutationsToLocalStorage(mutations) {
    mutations.forEach((mutationName) => {
      // eslint-disable-next-line no-unused-expressions
      window.localStorage.getItem(`${this.module}/${mutationName}`) &&
        this.store.commit(
          `${this.module}/${mutationName}`,
          JSON.parse(
            window.localStorage.getItem(`${this.module}/${mutationName}`)
          )
        );

      this.store.subscribe((vuexMutation) => {
        if (vuexMutation.type === `${this.module}/${mutationName}`) {
          window.localStorage.setItem(
            `${this.module}/${mutationName}`,
            JSON.stringify(vuexMutation.payload)
          );
        }
      });
    });
  }

  loadItemsOnCurrentChange({ attribute, serverSide }) {
    this.onRouteParamIdChange((id) => {
      if (attribute) {
        this.store.dispatch(`${this.module}/setFilterAttribute`, {
          attribute,
          serverSide: !!serverSide,
          values: [id],
          useDelay: false,
        });
      } else {
        this.store.dispatch(`${this.module}/setUrlParamId`, id);
      }
    });
  }

  changeParamOnRouteParamsChange(field) {
    this.onRouteParamIdChange((id) => {
      this.store.dispatch(`${this.module}/setAttributesObj`, {
        [field]: {
          serverSide: true,
          values: [id],
        },
      });
      this.store.dispatch(`${this.module}/initItems`);
    });
  }

  changeParamOnRequestChange(field) {
    this.onActiveRequestChange((request) => {
      this.store.dispatch(`${this.module}/setAttributesObj`, {
        [field]: {
          serverSide: true,
          values: [request.property_id],
        },
      });
      this.store.dispatch(`${this.module}/initItems`);
    });
  }

  changeParamWhenOpenSensors(field) {
    this.store.subscribeAction((action) => {
      if (action.type === 'sensors/properties/addCurrent') {
        this.store.dispatch(`${this.module}/setAttributesObj`, {
          [field]: {
            serverSide: true,
            values: [action.payload.id],
          },
        });
        this.store.dispatch(`${this.module}/initItems`);
      }
    });
  }

  addItemsToAttributesObjRefWhenTheyAreLoaded({
    attribute,
    autocomplete,
    filter,
    serverSide,
  }) {
    this.store.subscribe((mutation) => {
      if (mutation.type === `${filter}/SET_ITEMS`) {
        this.store.dispatch(`${this.module}/setAttributesObjRef`, {
          attribute,
          autocomplete,
          serverSide,
          values: mutation.payload,
        });
      }
    });
  }

  initAttributeFiltersWhenAttributesObjRefIsLoaded({ loadingAttributes }) {
    this.store.dispatch(`${this.module}/setAttributesObjRefLoading`, false);
    if (loadingAttributes && loadingAttributes.length !== 0) {
      this.store.dispatch(`${this.module}/setAttributesObjRefLoading`, true);
      this.store.subscribe((mutation) => {
        if (mutation.type === `${this.module}/SET_ATTRIBUTES_OBJ_REF`) {
          if (
            maths.arrayContainsArray(
              Object.keys(mutation.payload),
              loadingAttributes
            )
          ) {
            this.store.dispatch(
              `${this.module}/setAttributesObjRefLoading`,
              false
            );
          }
        }
      });
    }
  }
}

export default BaseModulePlugins;
